import {
    Box,
    FormControl,
    Typography,
    Checkbox as JoyCheckbox,
    FormHelperText,
    Stack,
    Input,
    Grid,
    Card,
    Select,
    Option,
    Chip,
} from "@mui/joy";
import { Palette, Ruler, Maximize2 } from "lucide-react";
import { useGutter, type GutterScopeState } from "./hooks/useGutter";

type GutterType = "gutters" | "downspouts";

interface GutterConfig {
    type: GutterType;
    title: string;
    removeKey: keyof GutterScopeState;
    installKey: keyof GutterScopeState;
    colorKey: keyof GutterScopeState;
    sizeKey: keyof GutterScopeState;
    sizes: Array<{ label: string; value: string }>;
    removeLabel: string;
    installLabel: string;
    removeHelperText: string;
    installHelperText: string;
}

const GUTTER_CONFIGS: GutterConfig[] = [
    {
        type: "gutters",
        title: "Gutters",
        removeKey: "removeExistingGutters",
        installKey: "installNewGutters",
        colorKey: "gutterColor",
        sizeKey: "gutterSize",
        sizes: [
            { label: '5"', value: '5"' },
            { label: '6"', value: '6"' },
        ],
        removeLabel: "Remove Existing Gutters",
        installLabel: "Install New Gutters",
        removeHelperText: "Check this box if existing gutters need to be removed before installation",
        installHelperText: "Check this box if new gutters need to be installed for this project",
    },
    {
        type: "downspouts",
        title: "Downspouts",
        removeKey: "removeExistingDownspouts",
        installKey: "installNewDownspouts",
        colorKey: "downspoutColor",
        sizeKey: "downspoutSize",
        sizes: [
            { label: '2x3"', value: '2x3"' },
            { label: '3x4"', value: '3x4"' },
        ],
        removeLabel: "Remove Existing Downspouts",
        installLabel: "Install New Downspouts",
        removeHelperText: "Check this box if existing downspouts need to be removed before installation",
        installHelperText: "Check this box if new downspouts need to be installed for this project",
    },
];

export function GutterScope() {
    const { state, updateGutterState, isLoading } = useGutter();

    if (isLoading) {
        return (
            <Box mt={2}>
                <Typography level="body-sm">Loading gutter details...</Typography>
            </Box>
        );
    }

    return (
        <Box mt={2}>
            <Typography level="title-sm">Gutter Scope</Typography>
            <Typography level="body-sm">
                Gutter scope is the area of the roof that is covered by the gutters.
            </Typography>

            <Stack direction="column" spacing={2} mt={2}>
                {GUTTER_CONFIGS.map((config) => (
                    <GutterCard key={config.type} config={config} state={state} onUpdate={updateGutterState} />
                ))}
            </Stack>
        </Box>
    );
}

interface GutterCardProps {
    config: GutterConfig;
    state: GutterScopeState;
    onUpdate: <K extends keyof GutterScopeState>(key: K, value: GutterScopeState[K]) => void;
}

function GutterCard({ config, state, onUpdate }: GutterCardProps) {
    const isInstalling = state[config.installKey] as boolean;

    return (
        <Card variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography level="title-sm">{config.title}</Typography>
                {isInstalling && (
                    <Chip size="sm" variant="soft" color="success">
                        Quantity: {config.type === "gutters" ? state.gutterLength || 0 : state.downspoutLength || 0} LF
                    </Chip>
                )}
            </Box>
            <Stack direction="column" spacing={2}>
                <LocalCheckbox
                    label={config.removeLabel}
                    checked={state[config.removeKey] as boolean}
                    onChange={(checked) => onUpdate(config.removeKey, checked)}
                    size="sm"
                    helperText={config.removeHelperText}
                />

                <LocalCheckbox
                    label={config.installLabel}
                    checked={isInstalling}
                    onChange={(checked) => onUpdate(config.installKey, checked)}
                    size="sm"
                    helperText={config.installHelperText}
                />

                {isInstalling && (
                    <Box sx={{ ml: 2, mt: 1 }}>
                        <Grid container spacing={2}>
                            <Grid xs={5}>
                                <Typography level="body-sm" sx={{ mb: 1 }}>
                                    {config.title.slice(0, -1)} Color
                                </Typography>
                                <Input
                                    placeholder="Enter color"
                                    value={state[config.colorKey] as string}
                                    onChange={(e) => onUpdate(config.colorKey, e.target.value)}
                                    startDecorator={<Palette size={16} />}
                                    sx={{ width: "100%" }}
                                />
                            </Grid>
                            <Grid xs={5}>
                                <Typography level="body-sm" sx={{ mb: 1 }}>
                                    {config.title.slice(0, -1)} Length (LF)
                                </Typography>
                                <Input
                                    placeholder={`Total ${config.title.toLowerCase()} length`}
                                    value={
                                        config.type === "gutters"
                                            ? (state.gutterLength as string)
                                            : (state.downspoutLength as string)
                                    }
                                    onChange={(e) =>
                                        onUpdate(
                                            config.type === "gutters" ? "gutterLength" : "downspoutLength",
                                            e.target.value
                                        )
                                    }
                                    startDecorator={<Ruler size={16} />}
                                    sx={{ width: "100%" }}
                                />
                            </Grid>
                            <Grid xs={2}>
                                <Typography level="body-sm" sx={{ mb: 1 }}>
                                    {config.title.slice(0, -1)} Size
                                </Typography>
                                <Select
                                    sx={{ width: "100%" }}
                                    placeholder="Select size"
                                    value={state[config.sizeKey] as string}
                                    onChange={(_event, value) => onUpdate(config.sizeKey, value || "")}
                                    startDecorator={<Maximize2 size={16} />}
                                >
                                    {config.sizes.map((size) => (
                                        <Option key={size.value} value={size.value}>
                                            {size.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </Stack>
        </Card>
    );
}

function LocalCheckbox({
    label,
    checked,
    onChange,
    helperText,
    size = "md",
}: {
    label: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    helperText?: string;
    size?: "sm" | "md" | "lg";
}) {
    return (
        <FormControl size={size}>
            <JoyCheckbox
                checked={checked}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.checked)}
                label={label}
                size={size}
            />
            {helperText && (
                <FormHelperText>
                    <Typography level="body-xs" sx={{ color: "text.secondary" }}>
                        {helperText}
                    </Typography>
                </FormHelperText>
            )}
        </FormControl>
    );
}
